import { ScanOrScanTemplatePartTypeSchema } from '../CommonScanSchemas';
import { OptimizedValueExpressionsBase } from '../OptimizedValueExpressions';
import { ScanAudio, ScanGroup, ScanLineItem, ScanOption, ScanPart, ScanPhoto, ScanRoot, ScanTask, ScanValues, ScanVideo } from '../ScanRoot';
import { ScanTemplateGroup, ScanTemplateLineItem, ScanTemplateOption, ScanTemplatePart, ScanTemplateRoot, ScanTemplateTask } from '../ScanTemplateRoot';

export type PhotoIdUse = {
  photoId: string;
  scanPartType: ScanOrScanTemplatePartTypeSchema;
  groupId?: string;
  taskId?: string;
  optionId?: string;
  lineItemId?: string;
};

// ************************************************************************
// Scan Optimized Schema
// ************************************************************************
// The optimized schema is used in memory by the UX streamline presentation and updates
// De-optimizing will return only what needs to be saved to the database
export type ScanStatus = 'notStarted' | 'inProgress' | 'completed';

/**
 * ReducedValues can and will be a sum of all numerical values in any part of the sub-structure
 * We call our a few special cases below for convenient typescript
 */
export type ReducedValues = {
  materialCost?: number;
  laborCost?: number;
  roomArea?: number;
};

export type PhotoIdUsesByPhotoId = Record<string, PhotoIdUse[]>;

export type AudioIdUse = {
  audioId: string;
  scanPartType: ScanOrScanTemplatePartTypeSchema;
  groupId?: string;
  taskId?: string;
  optionId?: string;
  lineItemId?: string;
};

export type AudioIdUsesByAudioId = Record<string, AudioIdUse[]>;

/**
 * This is the optimized version of the ScanPart. It is used in memory by the UX streamline presentation and updates
 * REFACTOR in progress: migrating all optimized fields to the optimized object. This will make it much easier to de-optimize
 * and to understand which fields are the optimized fields.
 */
export type ScanPartOptimizedDetails<TemplateType extends ScanTemplatePart = ScanTemplatePart> = {
  allPhotoIds: string[];
  allPhotos: ScanPhoto[];
  allPhotoIdUsesByPhotoId: PhotoIdUsesByPhotoId;
  allVideoIds: string[];
  allVideos: ScanVideo[];
  allAudioIds: string[];
  allAudio: ScanAudio[];
  allAudioIdUsesByAudioId: AudioIdUsesByAudioId;
  optimizedValueExpressions: OptimizedValueExpressionsBase;
  reducedValues: ReducedValues; // reduced from this.values and all this.subParts[].reducedValues
  searchText: string;
};
export type ScanPartOptimized<TemplateType extends ScanTemplatePart = ScanTemplatePart> = Omit<ScanPart, 'values'> & {
  values: ScanValues; // values guaranteed to be set when Optimized
  scanStatus: ScanStatus;
  template: TemplateType;
  scanPartType: ScanOrScanTemplatePartTypeSchema;

  optimized: ScanPartOptimizedDetails<TemplateType>;
};

// scanPartType, optimizedValueExpressions, lineItems, lineItemsById, template, scanStatus, reducedValues, allPhotoIds, allPhotoIdUsesByPhotoId
export const SCAN_PART_OPTIMIZED_FIELDS_TO_REMOVE = [
  'optimizedValueExpressions',
  'scanStatus',
  'reducedValues',
  'allPhotoIds',
  'allPhotos',
  'allVideoIds',
  'allVideos',
  'allAudioIds',
  'allAudio',
  'allAudioIdUsesByAudioId',
  'template',
  'scanPartType',
  'lineItemsById',
  'optionsById',
  'tasksById',
  'groupsById',
  'optimized',
];

export type ScanLineItemOptimized = ScanLineItem & ScanPartOptimized<ScanTemplateLineItem>;
export type ScanOptionOptimized = Omit<ScanOption, 'lineItems'> & ScanPartOptimized<ScanTemplateOption> & {
  lineItems: ScanLineItemOptimized[];
  lineItemsById: Record<string, ScanLineItemOptimized>;
};
export type ScanTaskOptimized = Omit<ScanTask, 'options'> & ScanPartOptimized<ScanTemplateTask> & {
  options: ScanOptionOptimized[];
  optionsById: Record<string, ScanOptionOptimized>;
};
export type ScanGroupOptimized = Omit<ScanGroup, 'tasks'> & ScanPartOptimized<ScanTemplateGroup> & {
  tasks: ScanTaskOptimized[];
  tasksById: Record<string, ScanTaskOptimized>;
};
export type ScanRootOptimized = Omit<ScanRoot, 'groups'> & ScanPartOptimized<ScanTemplateRoot> & {
  groups: ScanGroupOptimized[];
  groupsById: Record<string, ScanGroupOptimized>;
};

/** @deprecated - use ScanRootOptimized instead */
export type ScanOptimized = ScanRootOptimized;
